import { useEffect, useState } from 'preact/hooks';
import { QUOTE_CURRENCIES, type QuotesPageBody, type QuoteView } from '../../shared/quotes/rules';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiGet, apiSend, apiUpload } from '../lib/api';
import { formatDate } from '../lib/dates';

// Quotes and comparison (P2.7). Procurement records each supplier's quote line by line, sees
// totals and the price check against the last awarded price, and chooses one. Fewer valid
// quotes than required need a reason (D50). The broker computes totals and flags.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; data: QuotesPageBody };

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

interface Draft {
  supplierId: string;
  quoteRef: string;
  currency: string;
  validUntil: string;
  leadTime: string;
  prices: string[];
}

export function QuotesPage({ prNo }: { prNo: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});

  useEffect(() => {
    let live = true;
    apiGet<QuotesPageBody>(`/api/prs/${encodeURIComponent(prNo)}/quotes`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [prNo, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>{load.code === 'forbidden' ? t('review.error.forbidden') : t('req.loadError')}</p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
      </section>
    );
  }

  const { data } = load;
  const base = `/api/prs/${encodeURIComponent(prNo)}/quotes` as const;
  const quoted = new Set(data.quotes.map((q) => q.supplierId));
  const waiting = data.suppliers.filter((s) => s.rfqStatus === 'sent' && !quoted.has(s.supplierId));
  const declined = data.suppliers.filter(
    (s) => s.rfqStatus === 'declined' && !quoted.has(s.supplierId),
  );

  async function run(fn: () => Promise<unknown>, done: MessageKey) {
    setBusy(true);
    setNote(null);
    setFields({});
    try {
      await fn();
      setNote({ ok: true, text: t(done) });
      setReload((r) => r + 1);
      return true;
    } catch (err) {
      const e = err instanceof ApiError ? err : null;
      setFields(e?.fields ?? {});
      const known = ['invalid_input', 'invalid_transition', 'conflict', 'forbidden'];
      setNote({
        ok: false,
        text:
          e?.code === 'invalid_input' && e.fields.reason
            ? e.fields.reason
            : t(
                (known.includes(e?.code ?? '')
                  ? `quote.error.${e?.code ?? ''}`
                  : 'review.error.generic') as MessageKey,
              ),
      });
      return false;
    } finally {
      setBusy(false);
    }
  }

  const startDraft = (q?: QuoteView) =>
    setDraft({
      supplierId: q?.supplierId ?? waiting[0]?.supplierId ?? '',
      quoteRef: q?.quoteRef ?? '',
      currency: q?.currency ?? data.currency,
      validUntil: q?.validUntil ?? '',
      leadTime: q?.leadTime ?? '',
      prices: data.lines.map((_, i) => (q ? String((q.lines[i]?.unitPrice ?? 0) / 100) : '')),
    });

  const save = (d: Draft) =>
    void run(async () => {
      let name = '';
      if (file) {
        const up = await apiUpload<{ name: string }>(
          `${base}/file?supplier=${encodeURIComponent(d.supplierId)}`,
          file,
        );
        name = up.name;
      }
      await apiSend('POST', base, {
        supplierId: d.supplierId,
        quoteRef: d.quoteRef,
        currency: d.currency,
        validUntil: d.validUntil,
        leadTime: d.leadTime,
        unitPrices: d.prices.map((p) => Number(p.replace(/,/g, ''))),
        file: name,
      });
    }, 'quote.done.saved').then((ok) => {
      if (ok) {
        setDraft(null);
        setFile(null);
      }
    });

  const draftTotal = draft
    ? data.lines.reduce(
        (sum, l, i) => sum + Math.round(l.qty * Math.round(Number(draft.prices[i] || 0) * 100)),
        0,
      )
    : 0;

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/queue">{t('nav.queue')}</a> /{' '}
        <a href={`#/requests/${prNo}`} dir="ltr">
          {prNo}
        </a>{' '}
        / <a href={`#/requests/${prNo}/rfq`}>RFQ</a> / {t('quote.crumb')}
      </p>
      <div class="detail-head">
        <span class={`pill pill--${data.rule.needsReason ? 'warning' : 'success'}`}>
          {t('quote.rule', { n: String(data.rule.count), required: String(data.rule.required) })}
        </span>
        {data.rush && <span class="pill pill--warning">{t('req.rush')}</span>}
      </div>
      <h2 class="detail-title">{data.title}</h2>
      {!data.canEdit && <p class="notice notice--action">{t('quote.notSourcing')}</p>}
      {data.canEdit && data.quotes.some((q) => q.selected) && (
        <div class="actions">
          <a class="button button--primary" href={`#/requests/${prNo}/po`}>
            {t('po.open')}
          </a>
        </div>
      )}
      {note && (
        <p class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          {note.text}
        </p>
      )}

      <section class="card" aria-labelledby="q-compare">
        <div class="card__head">
          <h3 id="q-compare" class="card__title">
            {t('quote.compare')}
          </h3>
          <span class="caption">{t('quote.varianceHint', { pct: String(data.variancePct) })}</span>
        </div>
        {data.quotes.length === 0 ? (
          <p>{t('quote.none')}</p>
        ) : (
          <div class="table-wrap">
            <table class="table">
              <thead>
                <tr>
                  <th scope="col">{t('inv.col.supplier')}</th>
                  <th scope="col" class="num">
                    {t('quote.col.total')}
                  </th>
                  <th scope="col">{t('quote.col.valid')}</th>
                  <th scope="col">{t('quote.col.lead')}</th>
                  <th scope="col">{t('quote.col.price')}</th>
                  <th scope="col">
                    <span class="visually-hidden">{t('admin.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.quotes.map((q) => (
                  <tr key={q.quoteId}>
                    <td>
                      <span class="req-row__title">{q.supplierName}</span>
                      <span class="caption cell-sub">
                        {q.quoteRef && <span dir="ltr">{q.quoteRef}</span>}
                        {q.file && ` · ${t('quote.fileOnFile')}`}
                      </span>
                      {q.selected && <span class="pill pill--success">{t('quote.chosen')}</span>}
                    </td>
                    <td class="num" dir="ltr">
                      {q.currency} {money(q.amount)}
                    </td>
                    <td>
                      {formatDate(q.validUntil, lang)}
                      {q.expired && <span class="pill pill--danger">{t('quote.expired')}</span>}
                    </td>
                    <td dir="auto">{q.leadTime || '—'}</td>
                    <td>
                      {q.flagged ? (
                        <span class="pill pill--warning">
                          {t('quote.above', { pct: String(q.maxVariancePct ?? '') })}
                        </span>
                      ) : q.maxVariancePct === null ? (
                        <span class="caption">{t('quote.noHistory')}</span>
                      ) : (
                        <span class="pill pill--success">
                          {t('quote.priceOk', { pct: String(q.maxVariancePct) })}
                        </span>
                      )}
                      <details class="quote-lines">
                        <summary>{t('quote.lines')}</summary>
                        <ul>
                          {q.lines.map((l, i) => (
                            <li key={i}>
                              <span dir="auto">{l.description}</span> ·{' '}
                              <span dir="ltr">
                                {l.qty} × {money(l.unitPrice)}
                              </span>
                              {l.lastPrice !== null && (
                                <span class={l.flagged ? 'quote-flag' : 'caption'}>
                                  {' '}
                                  {t('quote.last', {
                                    price: money(l.lastPrice),
                                    date: formatDate(l.lastDate, lang),
                                    pct: String(l.variancePct ?? ''),
                                  })}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    </td>
                    <td>
                      {data.canEdit && !q.selected && (
                        <div class="actions">
                          <button
                            type="button"
                            class="button button--small button--primary"
                            disabled={busy || q.expired}
                            aria-label={t('quote.chooseFor', { name: q.supplierName })}
                            onClick={() =>
                              void run(
                                () =>
                                  apiSend('POST', `${base}/select`, {
                                    quoteId: q.quoteId,
                                    etag: q.etag,
                                    reason,
                                  }),
                                'quote.done.chosen',
                              )
                            }
                          >
                            {t('quote.choose')}
                          </button>
                          <button
                            type="button"
                            class="button button--small"
                            disabled={busy}
                            aria-label={t('quote.editFor', { name: q.supplierName })}
                            onClick={() => startDraft(q)}
                          >
                            {t('quote.edit')}
                          </button>
                        </div>
                      )}
                      {q.selected && q.exceptionReason && (
                        <span class="caption" dir="auto">
                          {q.exceptionReason}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.canEdit && data.rule.needsReason && data.quotes.length > 0 && (
          <div class="field">
            <label for="q-reason">
              {t('quote.reason', { required: String(data.rule.required) })}
            </label>
            <textarea
              id="q-reason"
              rows={2}
              dir="auto"
              maxLength={500}
              value={reason}
              aria-invalid={fields.reason ? true : undefined}
              onInput={(e) => setReason((e.target as HTMLTextAreaElement).value)}
            />
            <p class="caption">{t('quote.reasonHint')}</p>
          </div>
        )}
      </section>

      {data.canEdit && (waiting.length > 0 || declined.length > 0) && (
        <section class="card" aria-labelledby="q-waiting">
          <h3 id="q-waiting" class="card__title">
            {t('quote.waiting')}
          </h3>
          <ul class="action-list">
            {waiting.map((s) => (
              <li key={s.supplierId} class="actions">
                <span>{s.name}</span>
                <button
                  type="button"
                  class="button button--small"
                  disabled={busy}
                  aria-label={t('quote.noResponseFor', { name: s.name })}
                  onClick={() =>
                    void run(
                      () => apiSend('POST', `${base}/no-response`, { supplierId: s.supplierId }),
                      'quote.done.noResponse',
                    )
                  }
                >
                  {t('quote.noResponse')}
                </button>
              </li>
            ))}
            {declined.map((s) => (
              <li key={s.supplierId}>
                {s.name} · <span class="caption">{t('quote.declined')}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {data.canEdit &&
        (draft ? (
          <form
            class="card stack"
            noValidate
            aria-labelledby="q-add"
            onSubmit={(e) => {
              e.preventDefault();
              save(draft);
            }}
          >
            <h3 id="q-add" class="card__title">
              {t('quote.add')}
            </h3>
            <div class="reg-grid">
              <div class="field">
                <label for="q-supplier">{t('inv.col.supplier')}</label>
                <select
                  id="q-supplier"
                  value={draft.supplierId}
                  aria-invalid={fields.supplierId ? true : undefined}
                  onChange={(e) =>
                    setDraft({ ...draft, supplierId: (e.target as HTMLSelectElement).value })
                  }
                >
                  <option value="">{t('pr.select')}</option>
                  {data.suppliers.map((s) => (
                    <option key={s.supplierId} value={s.supplierId}>
                      {s.name}
                      {s.rfqStatus ? ` · ${t('quote.asked')}` : ''}
                    </option>
                  ))}
                </select>
                {fields.supplierId && <p class="field__error">{fields.supplierId}</p>}
              </div>
              <div class="field">
                <label for="q-ref">{t('quote.ref')}</label>
                <input
                  id="q-ref"
                  type="text"
                  dir="ltr"
                  maxLength={60}
                  value={draft.quoteRef}
                  onInput={(e) =>
                    setDraft({ ...draft, quoteRef: (e.target as HTMLInputElement).value })
                  }
                />
              </div>
              <div class="field">
                <label for="q-currency">{t('quote.currency')}</label>
                <select
                  id="q-currency"
                  value={draft.currency}
                  onChange={(e) =>
                    setDraft({ ...draft, currency: (e.target as HTMLSelectElement).value })
                  }
                >
                  {QUOTE_CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div class="field">
                <label for="q-valid">{t('quote.col.valid')}</label>
                <input
                  id="q-valid"
                  type="date"
                  dir="ltr"
                  value={draft.validUntil}
                  aria-invalid={fields.validUntil ? true : undefined}
                  aria-describedby="q-valid-when"
                  onInput={(e) =>
                    setDraft({ ...draft, validUntil: (e.target as HTMLInputElement).value })
                  }
                />
                <DateHint id="q-valid-when" value={draft.validUntil} />
                {fields.validUntil && <p class="field__error">{fields.validUntil}</p>}
              </div>
              <div class="field">
                <label for="q-lead">{t('quote.col.lead')}</label>
                <input
                  id="q-lead"
                  type="text"
                  dir="auto"
                  maxLength={60}
                  value={draft.leadTime}
                  onInput={(e) =>
                    setDraft({ ...draft, leadTime: (e.target as HTMLInputElement).value })
                  }
                />
              </div>
            </div>
            <fieldset class="reg-group">
              <legend>{t('quote.unitPrices', { currency: draft.currency })}</legend>
              {data.lines.map((l, i) => (
                <div class="field quote-price" key={i}>
                  <label for={`q-price-${i}`}>
                    <span dir="auto">{l.description}</span>{' '}
                    <span class="caption" dir="ltr">
                      × {l.qty}
                    </span>
                  </label>
                  <input
                    id={`q-price-${i}`}
                    type="text"
                    inputMode="decimal"
                    dir="ltr"
                    maxLength={14}
                    value={draft.prices[i] ?? ''}
                    onInput={(e) => {
                      const prices = [...draft.prices];
                      prices[i] = (e.target as HTMLInputElement).value;
                      setDraft({ ...draft, prices });
                    }}
                  />
                </div>
              ))}
              {fields.unitPrices && <p class="field__error">{fields.unitPrices}</p>}
              <p>
                <strong>{t('quote.col.total')}:</strong>{' '}
                <span dir="ltr">
                  {draft.currency} {money(draftTotal)}
                </span>
              </p>
            </fieldset>
            <div class="field">
              <label for="q-file">{t('quote.file')}</label>
              <input
                id="q-file"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx"
                onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
              />
              {(fields.file ?? '') && <p class="field__error">{fields.file}</p>}
            </div>
            <div class="actions">
              <button
                type="submit"
                class="button button--primary"
                disabled={busy || !draft.supplierId}
              >
                {t('quote.save')}
              </button>
              <button type="button" class="button" onClick={() => setDraft(null)}>
                {t('sup.bank.changeClose')}
              </button>
            </div>
          </form>
        ) : (
          <div class="actions">
            <button type="button" class="button button--primary" onClick={() => startDraft()}>
              {t('quote.add')}
            </button>
          </div>
        ))}
      {!data.quotes.length && waiting.length === 0 && declined.length === 0 && (
        <p class="caption">{t('quote.noRfq')}</p>
      )}
    </div>
  );
}
